/**
 * Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: LicenseRef-.amazon.com.-AmznSL-1.0
 * Licensed under the Amazon Software License  http://aws.amazon.com/asl/
 */

import _ from 'lodash';
import { createForm } from '../../helpers/form';

const editWorkflowTemplateDraftMetaFields = templateVersion => {
  const { title = '', desc = '', instanceTtl, runSpec = {}, propertyOverrideSummaryRows = [] } = templateVersion;
  const result = [
    {
      name: 'templateTitle',
      label: 'Workflow Template Title',
      placeholder: 'Type a title for the workflow template',
      extra: {
        explain: `This is a required field and the number of characters must be between 3 and 255.
      The title is shown in many places in the UI.`,
      },
      value: title,
      rules: 'required|string|between:3,255',
    },

    {
      name: 'templateDesc',
      label: 'Workflow Template Description',
      placeholder: 'Type a description for the workflow template',
      extra: {
        explain: `The description can be written in markdown but must be between 3 and 4000 characters.`,
      },
      value: desc,
      rules: 'required|string|between:3,4000',
    },

    {
      name: 'instanceTtl',
      label: 'Time to Live (TTL) for instances of the workflow',
      placeholder: 'Type the number of days',
      extra: {
        explain: `The number of days for which a record of a workflow instance is kept in the database.
      Leave it empty or type -1 if you don't want to have a time limit on the record.`,
      },
      value: instanceTtl,
      rules: 'integer',
    },

    {
      name: 'runSpecSize',
      label: 'Runtime lambda size',
      extra: {
        options: [
          {
            value: 'small',
            text: 'Small',
          },
          {
            value: 'medium',
            text: 'Medium',
          },
          {
            value: 'large',
            text: 'Large',
          },
        ],
      },
      value: runSpec.size || 'small',
      rules: 'required|in:small,medium,large',
    },

    {
      name: 'runSpecTarget',
      label: 'Runtime target',
      extra: {
        options: [
          {
            value: 'stepFunctions',
            text: 'Step Functions',
          },
          {
            value: 'workerLambda',
            text: 'Worker Lambda',
          },
          {
            value: 'inPlace',
            text: 'In Place',
          },
        ],
      },
      value: runSpec.target || 'stepFunctions',
      rules: 'required|in:stepFunctions,workerLambda,inPlace',
    },
  ];

  const propsOverrideFields = _.map(propertyOverrideSummaryRows, ({ name, title_, allowed = false }) => ({
    name: `propsOverride_${name}`,
    label: title_,
    value: allowed,
    default: allowed,
  }));

  return [...result, ...propsOverrideFields];
};

function getEditWorkflowTemplateDraftMetaForm(template) {
  const fields = editWorkflowTemplateDraftMetaFields(template);

  return createForm(fields);
}

export default getEditWorkflowTemplateDraftMetaForm;
